import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { InputField } from "@/components/podkit/forms/InputField";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { Text } from "@/components/podkit/typography/Text";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Switch } from "@/components/Switch";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdateSCIMConfiguration, type PlainSCIMConfiguration } from "@/queries/scim-queries";
import { useListSSOConfigurations } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { MoreHorizontal } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";

type SCIMConfigCardProps = {
    config: PlainSCIMConfiguration;
    editable: boolean;
    onEdit: () => void;
    onDelete: () => void;
    onRegenerateToken: () => void;
};

export const SCIMConfigCard: FC<SCIMConfigCardProps> = ({ config, editable, onEdit, onDelete, onRegenerateToken }) => {
    const { data: ssoConfigs = [] } = useListSSOConfigurations();

    // Find linked SSO configuration name
    const linkedSSOName = useMemo(() => {
        if (!config.ssoConfigurationId) return null;
        const ssoConfig = ssoConfigs.find((sso) => sso.id === config.ssoConfigurationId);
        return ssoConfig?.displayName || ssoConfig?.issuerUrl || "Unknown SSO";
    }, [config.ssoConfigurationId, ssoConfigs]);

    const scimEndpoint = `${window.location.origin}/scim/v2`;

    return (
        <Card className="flex w-full flex-col gap-4 bg-surface-glass p-4" variant="bordered">
            <div className="flex flex-wrap items-center gap-3">
                <EnablementToggle config={config} editable={editable} />
                <div className="flex flex-1 flex-wrap items-center gap-2">
                    <Text className="text-base font-medium text-content-primary">
                        {config.name || "SCIM Configuration"}
                    </Text>
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={onEdit}
                        disabled={!editable}
                        data-tracking-id="edit-scim-config-card"
                    >
                        Edit
                    </Button>
                    <DropdownActions
                        button={
                            <button
                                className="flex size-8 items-center justify-center rounded-md hover:bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
                                aria-label="SCIM configuration actions"
                                disabled={!editable}
                                data-tracking-id="scim-config-actions"
                            >
                                <MoreHorizontal className="size-4 text-content-tertiary" />
                            </button>
                        }
                        align="end"
                    >
                        <DropdownMenuItem
                            key="regenerate"
                            onClick={onRegenerateToken}
                            disabled={!editable}
                            data-tracking-id="regenerate-scim-token"
                        >
                            Regenerate Token
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            key="delete"
                            variant="destructive"
                            onClick={onDelete}
                            disabled={!editable}
                            data-tracking-id="delete-scim-config"
                        >
                            Delete
                        </DropdownMenuItem>
                    </DropdownActions>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <InputField label="SCIM endpoint URL" >
                    <CopyableInput value={scimEndpoint} disabled data-tracking-id="copy-scim-endpoint" />
                </InputField>
                {linkedSSOName && (
                    <Text className="text-xs text-content-tertiary">Linked to: {linkedSSOName}</Text>
                )}
            </div>
        </Card>
    );
};

/**
 * Toggle component for enabling/disabling SCIM provisioning
 * Shows confirmation dialog before disabling
 */
const EnablementToggle: FC<{ config: PlainSCIMConfiguration; editable: boolean }> = ({ config, editable }) => {
    const updateMutation = useUpdateSCIMConfiguration();
    const { toast } = useToast();
    const [showConfirm, setShowConfirm] = useState(false);

    const handleToggle = useCallback(async () => {
        if (config.enabled) {
            // Show confirmation before disabling
            setShowConfirm(true);
        } else {
            // Enable directly
            try {
                await updateMutation.mutateAsync({
                    scimConfigurationId: config.id,
                    enabled: true,
                });
                toast({
                    title: "SCIM provisioning enabled",
                });
            } catch (error) {
                toast({
                    title: "Failed to enable SCIM provisioning",
                    description: formatError(error),
                });
            }
        }
    }, [config.enabled, config.id, toast, updateMutation]);

    const handleDisable = useCallback(async () => {
        try {
            await updateMutation.mutateAsync({
                scimConfigurationId: config.id,
                enabled: false,
            });
            toast({
                title: "SCIM provisioning disabled",
            });
            setShowConfirm(false);
        } catch (error) {
            toast({
                title: "Failed to disable SCIM provisioning",
                description: formatError(error),
            });
        }
    }, [config.id, toast, updateMutation]);

    return (
        <>
            <Switch
                state={config.enabled ? "checked" : "unchecked"}
                onToggle={handleToggle}
                disabled={!editable || updateMutation.isPending}
                id={`toggle-scim-${config.id}`}
            />

            {showConfirm && (
                <Dialog open onOpenChange={setShowConfirm}>
                    <DialogContent data-track-location={TrackLocations.SCIMConfiguration}>
                        <DialogHeader>
                            <DialogTitle>Disable SCIM provisioning?</DialogTitle>
                            <DialogDescription />
                        </DialogHeader>
                        <DialogBody>
                            <Text className="text-base">
                                Users will no longer be automatically provisioned or deprovisioned via SCIM.
                            </Text>
                            <Text className="text-base">
                                Existing users will remain in the organization and will need to be managed manually.
                            </Text>
                        </DialogBody>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button variant="outline" autoFocus>
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                variant="destructive"
                                onClick={handleDisable}
                                loading={updateMutation.isPending}
                                disabled={updateMutation.isPending}
                                data-tracking-id="confirm-disable-scim"
                            >
                                Disable
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};
